import { useState, useEffect, useRef } from 'react';
import { CreditCardIcon, BanknotesIcon, ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import { paymentApi } from '../../services/api';
import { useVerificationStore } from '../../stores/verificationStore';
import { Alert } from '../shared/Alert';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import type { BankingDetails } from '../../types';

interface PaymentStepProps {
  onComplete: () => void;
}

export function PaymentStep({ onComplete }: PaymentStepProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<'EFT' | 'Card'>('EFT');
  const [payFastEnabled, setPayFastEnabled] = useState(false);
  const [bankingDetails, setBankingDetails] = useState<BankingDetails | null>(null);
  const [referenceNumber, setReferenceNumber] = useState<string | null>(null);
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [paymentData, setPaymentData] = useState<{
    redirectUrl: string;
    formData: Record<string, string>;
  } | null>(null);
  const { requestId } = useVerificationStore();

  // Check if PayFast is enabled
  useEffect(() => {
    paymentApi.getConfig().then(res => {
      setPayFastEnabled(res.data.payFastEnabled);
    }).catch(() => {
      setPayFastEnabled(false);
    });
  }, []);

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const initiatePayment = async () => {
    if (!requestId) return;
    setError(null);
    setIsLoading(true);

    try {
      const response = await paymentApi.initiate(requestId, selectedMethod);

      if (selectedMethod === 'EFT' && response.data.bankingDetails) {
        // Manual EFT flow: show banking details
        setBankingDetails(response.data.bankingDetails);
        setReferenceNumber(response.data.referenceNumber);
        setPaymentSubmitted(true);
      } else if (response.data.redirectUrl && response.data.formData) {
        // PayFast flow: redirect
        setPaymentData({
          redirectUrl: response.data.redirectUrl,
          formData: response.data.formData,
        });
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to initiate payment');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (paymentData && formRef.current) {
      formRef.current.submit();
    }
  }, [paymentData]);

  // After EFT payment initiated: show banking details screen
  if (paymentSubmitted && bankingDetails) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">EFT Payment Details</h2>
          <p className="text-sm text-gray-600 mt-1">
            Please make a bank transfer using the details below.
          </p>
        </div>

        {/* Banking Details Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-6">
          <h3 className="font-semibold text-blue-900 mb-4">Banking Details</h3>
          <div className="space-y-3">
            {[
              { label: 'Bank', value: bankingDetails.bankName },
              { label: 'Account Name', value: bankingDetails.accountName },
              { label: 'Account Number', value: bankingDetails.accountNumber, copyable: true },
              { label: 'Branch Code', value: bankingDetails.branchCode, copyable: true },
              { label: 'Account Type', value: bankingDetails.accountType },
              { label: 'Amount', value: `R${bankingDetails.amount.toFixed(2)}` },
            ].map(({ label, value, copyable }) => (
              <div key={label} className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                <span className="text-sm text-blue-700">{label}:</span>
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-blue-900">{value}</span>
                  {copyable && (
                    <button
                      onClick={() => copyToClipboard(value, label)}
                      className="text-blue-500 hover:text-blue-700 p-1"
                      title={`Copy ${label}`}
                    >
                      {copiedField === label ? (
                        <CheckIcon className="h-4 w-4 text-green-600" />
                      ) : (
                        <ClipboardDocumentIcon className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reference Number - Highlighted */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-medium text-red-800">Payment Reference (IMPORTANT)</p>
              <p className="text-2xl font-bold text-red-900 mt-1">{referenceNumber}</p>
            </div>
            <button
              onClick={() => copyToClipboard(referenceNumber!, 'reference')}
              className="text-red-500 hover:text-red-700 p-2"
            >
              {copiedField === 'reference' ? (
                <CheckIcon className="h-5 w-5 text-green-600" />
              ) : (
                <ClipboardDocumentIcon className="h-5 w-5" />
              )}
            </button>
          </div>
          <p className="text-sm text-red-700 mt-2">{bankingDetails.referenceInstructions}</p>
        </div>

        <Alert
          type="info"
          message="Once payment has been received, your verification will be processed. You will receive an email confirmation when your payment has been matched."
        />

        <button
          type="button"
          onClick={onComplete}
          className="btn-primary w-full py-3 text-lg"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  // Default: payment method selection screen
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Payment</h2>
        <p className="text-sm text-gray-600 mt-1">
          Select your preferred payment method to complete your verification request.
        </p>
      </div>

      {error && <Alert type="error" message={error} />}

      <div className="space-y-4">
        {/* Payment Methods */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* EFT - Primary */}
          <button
            type="button"
            onClick={() => setSelectedMethod('EFT')}
            className={`p-4 border-2 rounded-lg text-left transition-colors ${
              selectedMethod === 'EFT'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-3">
              <BanknotesIcon
                className={`h-8 w-8 ${
                  selectedMethod === 'EFT' ? 'text-primary-600' : 'text-gray-400'
                }`}
              />
              <div>
                <p className="font-medium text-gray-900">EFT / Bank Transfer</p>
                <p className="text-sm text-gray-500">Manual bank transfer</p>
              </div>
            </div>
          </button>

          {/* Card - Coming Soon / Disabled */}
          <div
            className={`p-4 border-2 rounded-lg text-left relative ${
              payFastEnabled
                ? selectedMethod === 'Card'
                  ? 'border-primary-500 bg-primary-50 cursor-pointer'
                  : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                : 'border-gray-200 opacity-50 cursor-not-allowed'
            }`}
            onClick={() => payFastEnabled && setSelectedMethod('Card')}
          >
            <div className="flex items-center space-x-3">
              <CreditCardIcon
                className={`h-8 w-8 ${
                  payFastEnabled && selectedMethod === 'Card' ? 'text-primary-600' : 'text-gray-400'
                }`}
              />
              <div>
                <p className="font-medium text-gray-900">Credit/Debit Card</p>
                <p className="text-sm text-gray-500">
                  {payFastEnabled ? 'Visa, Mastercard, Amex' : 'Coming Soon'}
                </p>
              </div>
            </div>
            {!payFastEnabled && (
              <span className="absolute top-2 right-2 bg-gray-200 text-gray-600 text-xs px-2 py-1 rounded-full">
                Coming Soon
              </span>
            )}
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-4">Order Summary</h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Marital Status Verification</span>
              <span className="text-gray-900">R150.00</span>
            </div>
            <div className="border-t border-gray-200 pt-2 mt-2">
              <div className="flex justify-between font-medium">
                <span className="text-gray-900">Total</span>
                <span className="text-primary-600">R150.00</span>
              </div>
            </div>
          </div>
        </div>

        <Alert
          type="info"
          message={
            selectedMethod === 'EFT'
              ? 'You will be shown banking details to make a manual EFT payment. Once payment is received and confirmed, your verification will be processed.'
              : 'You will be redirected to PayFast to complete your payment securely. Your card details are never stored on our servers.'
          }
        />

        <button
          type="button"
          onClick={initiatePayment}
          disabled={isLoading || (!payFastEnabled && selectedMethod === 'Card')}
          className="btn-primary w-full py-3 text-lg"
        >
          {isLoading ? (
            <LoadingSpinner size="sm" />
          ) : selectedMethod === 'EFT' ? (
            'Proceed with EFT Payment'
          ) : (
            'Pay R150.00 with Card'
          )}
        </button>

        {/* Hidden PayFast form - kept for when PayFast is enabled */}
        {paymentData && (
          <form
            ref={formRef}
            action={paymentData.redirectUrl}
            method="POST"
            style={{ display: 'none' }}
          >
            {Object.entries(paymentData.formData).map(([key, value]) => (
              <input key={key} type="hidden" name={key} value={value} />
            ))}
          </form>
        )}
      </div>
    </div>
  );
}
